import { userName } from '@/utils/data/agents'
import { dateFormatFr, dateTimeFormatIso } from '@/utils/data/date'
import { computeQuotas } from '@/utils/data/pricings'
import { CURRENCY_EURO } from '@/consts/app'

const now = dateTimeFormatIso(new Date())

export const DATA_BASE_AGENCY_WEEK_DURATION = {
  1: 7, // lundi
  2: 7,
  3: 7,
  4: 7,
  5: 7
}

export const DATA_BASE_AGENCY_OVERTIME_INCREASE = [
  {
    increase: 25,
    overtime: 35
  },
  {
    increase: 50,
    overtime: 43
  }
]

export const DATA_BASE_AGENCY = {
  phone: [],
  address: {},
  emergencyNumbers: [],
  sampleDay: 5,
  startNightHours: '21:00',
  endNightHours: '06:00',
  utcSlug: 'brussels-copenhagen-madrid-paris',
  utcOffset: 120,
  currency: CURRENCY_EURO.value,
  specialties: [],
  catalogServices: [],
  agencySettingsFeature: {
    ptiFrequency: 30,
    photoService: true,
    regulatoryCalculation: false,
    breakTimeBilling: false,
    hoursLimit: 35,
    overtimeIncrease: DATA_BASE_AGENCY_OVERTIME_INCREASE,
    checkServiceMaxDuration: 1,
    serviceMaxDuration: 720,
    checkAgentRestDuration: 1,
    agentRestDuration: 660,
    checkSpaceBetweenServices: 1,
    spaceBetweenServices: 660,
    checkAgentOvertime: 1,
    agentOvertimeAllowed: 0,
    checkAgentMaxWeeklyHours: 1,
    agentMaxWeeklyHours: 48,
    checkAgentMinWeeklyRest: 1,
    agentMinWeeklyRest: 35,
    checkAgentQualified: 1,
    checkWeeklyConsecutiveRest: 1,
    weeklyConsecutiveRest: 1,
    checkMonthlySunday: 1,
    monthlySunday: 2,
    checkAgentRestriction: 1,
    checkAgentUnavailable: 1,
    ptiCheckFall: true,
    ptiCheckPerimeter: true,
    ptiCheckInactivity: true,
    ptiPauseOnBreak: true,
    weekDuration: {
      ...DATA_BASE_AGENCY_WEEK_DURATION
    },
    checkAgentHired: 1,
    overtimeCalculation: 1
  },
  cnapsCount: false,
  cnapsValue: 0.4,
  breaksNotWork: false,
  breaksNotCharge: false,
  agencyPricingSettings: {
    dayPrice: 21,
    nightFactor: 1.1,
    holidayFactor: 2.0,
    holidayNightFactor: 2.1,
    saturdayFactor: 1.1,
    saturdayNightFactor: 1.2,
    saturdayHolidayFactor: 2.1,
    saturdayHolidayNightFactor: 2.2,
    sundayFactor: 1.1,
    sundayNightFactor: 1.2,
    sundayHolidayFactor: 2.1,
    sundayHolidayNightFactor: 2.2,
    interventionPricing: 45,
    interventionLimit: null,
    interventionSpecialtyPricing: null,
    circuitPricing: 25,
    circuitLimit: null,
    circuitSpecialtyPricing: null
  },
  estimateSettings: {},
  invoiceSettings: {},
  eventReportSettings: {},
  interventionReportSettings: {},
  agentPlanningSettings: {
    weeklyMailing: true
  },
  customerPlanningSettings: {},
  agentExpirationSettings: {},
  agentExportSettings: {},
  agentItemsSettings: {},
  agencySettingsSubscription: {
    agentsLimit: 0
  },
  settingsContractAgent: {},
  settingsContractCustomer: {},
  requestTypes: [
    { name: 'Acompte sur salaire' },
    { name: 'Congé payé' },
    { name: 'Congés sans solde' },
    { name: 'Absence' },
    { name: 'Arrêt maladie' },
    { name: 'Formation' },
    { name: 'Entretien' },
    { name: 'Tenue et matériel' },
    { name: 'Autre' }
  ],
  rightsAgent: {},
  rightsCustomer: {}
}

export const DATA_AGENCIES_COLUMNS = [
  {
    name: 'name',
    label: 'Nom',
    format: (val, row) => [row.name, (row.phone || []).join(', '), row.email],
    sortable: true,
    classes: 'sticky',
    headerClasses: 'sticky'
  },
  {
    name: 'manager',
    label: 'Responsable',
    field: 'manager',
    format: (manager = {}) => [userName(manager), manager.phone, manager.email],
    sortable: true
  },
  {
    name: 'status',
    label: 'Statut',
    field: 'status',
    sortable: true
  },
  {
    name: 'subscription',
    label: 'Souscription',
    field: 'subscription',
    sort: (a = {}, b = {}, rowA = {}, rowB = {}) => {
      if (a.active === b.active) {
        if ((rowA.agencySettingsSubscription && rowA.agencySettingsSubscription.v3) > (rowB.agencySettingsSubscription && rowB.agencySettingsSubscription.v3)) {
          return -1
        }
      } else if (a.active < b.active) {
        return -1
      }
      return 1
    },
    format: (s = {}) => s.active || false,
    sortable: true
  },
  {
    name: 'quotas',
    label: 'Quotas',
    field: 'agencySettingsSubscription',
    sort: (a = {}, b = {}) => {
      const computedQuotasA = computeQuotas(a)
      const computedQuotasB = computeQuotas(b)
      const sign = Math.sign(computedQuotasA.agentsError - computedQuotasB.agentsError)
      return sign === 0
        ? Math.sign(computedQuotasA.accountsError - computedQuotasB.accountsError)
        : sign
    },
    format: computeQuotas,
    sortable: true
  },
  {
    name: 'paid',
    label: 'Paiement',
    field: 'subscription',
    sort: (a = {}, b = {}) => a.paid < b.paid ? -1 : 1,
    format: (s = {}) => s.paid || false,
    sortable: true
  },
  {
    name: 'expiration',
    label: 'Expiration',
    field: 'subscription',
    sort: (a = {}, b = {}) => a.currentPeriodEndAt < b.currentPeriodEndAt ? -1 : 1,
    format: (s = {}) => s.currentPeriodEndAt > now,
    sortable: true
  },
  {
    name: 'lastPayment',
    label: 'Dernier paiement',
    field: 'subscription',
    sortable: true
  },
  {
    name: 'createdAt',
    label: 'Date de création',
    field: 'createdAt',
    format: (v) => dateFormatFr(v),
    sortable: true
  }
]

export const DATA_AGENCIES_INDEXED_COLUMNS = _.keyBy(DATA_AGENCIES_COLUMNS, 'name')
